import BottomSheet, {
	BottomSheetFooter,
	BottomSheetTextInput,
	BottomSheetView,
} from "@gorhom/bottom-sheet";
import type { BottomSheetDefaultFooterProps } from "@gorhom/bottom-sheet/lib/typescript/components/bottomSheetFooter/types";
import { useAppTheme } from "@hooks/useAppTheme";
import { formatCount } from "@memorang/helpers/src/common";
import { Box, SheetHandle } from "@memorang/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Keyboard, Platform } from "react-native";
import { Button, Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type Props = {
	value?: string;
	onChangeText: (text: string) => void;
	placeholder: string;
	wordCount: number;
	maxLength?: number;
	disabled?: boolean;
};

const CustomBottomSheetTextInput = ({
	value,
	onChangeText,
	placeholder,
	wordCount,
	maxLength,
	disabled,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	const [snapIndex, setSnapIndex] = useState(0);

	const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
	const bottomSheetRef = useRef<BottomSheet>(null);

	useEffect(() => {
		const showSubscription = Keyboard.addListener("keyboardDidShow", () => {
			setIsKeyboardOpen(true);
			// Expand sheet so input and text stay above keyboard on Android
			bottomSheetRef.current?.snapToIndex(1);
		});
		const hideSubscription = Keyboard.addListener("keyboardDidHide", () => {
			setIsKeyboardOpen(false);
			bottomSheetRef.current?.snapToIndex(0);
		});

		return () => {
			showSubscription.remove();
			hideSubscription.remove();
		};
	}, []);

	const onBottomSheetChange = useCallback((index: number) => {
		setSnapIndex(index);

		// if (index === -1) {
		// 	Keyboard.dismiss();
		// }
	}, []);

	const { bottom } = useSafeAreaInsets();
	const bottomPadding = 80 + Math.max(bottom - 16, 0);

	const handleDismissBottomSheet = useCallback(() => {
		// bottomSheetRef.current?.close();
		Keyboard.dismiss();
	}, []);

	const renderFooter = useCallback(
		(props: BottomSheetDefaultFooterProps) => {
			return (
				<BottomSheetFooter
					{...props}
					style={{
						paddingTop: 8,
						alignItems: "flex-end",
						paddingBottom: 16,
						paddingRight: 16,
					}}
				>
					<Button
						mode="contained"
						icon="send"
						uppercase
						disabled={!value || disabled}
						onPress={handleDismissBottomSheet}
					>
						{t("writing.submit")}
					</Button>
				</BottomSheetFooter>
			);
		},
		[handleDismissBottomSheet, value, t, disabled],
	);

	return (
		<BottomSheet
			ref={bottomSheetRef}
			snapPoints={[220, "100%"]}
			handleComponent={() => (
				<Box
					paddingBottom={16}
					borderBottomWidth={1}
					borderBottomColor={theme.colors.divider}
				>
					<SheetHandle />
					<Box
						flexDirection="row"
						justifyContent="space-between"
						alignItems="center"
						paddingHorizontal={16}
					>
						<Text variant="bodyLarge">{t("writing.title")}</Text>
						<Text variant="bodyLarge">
							{t("writing.wordCount", {
								count: wordCount,
								word: formatCount(t("common.word"), wordCount),
							})}
						</Text>
					</Box>
				</Box>
			)}
			onChange={onBottomSheetChange}
			backgroundStyle={{
				borderStyle: "solid",
				borderWidth: 2,
				borderColor: theme.colors.divider,
				borderTopLeftRadius: 25,
				borderTopRightRadius: 25,
				backgroundColor: theme.colors.elevation.level1,
			}}
			footerComponent={
				isKeyboardOpen && Platform.OS === "ios" ? renderFooter : undefined
			}
			keyboardBlurBehavior="restore"
			enableDynamicSizing={false}
			keyboardBehavior="extend"
			android_keyboardInputMode="adjustResize"
		>
			<BottomSheetView
				style={{
					paddingBottom: isKeyboardOpen ? 0 : bottomPadding,
					paddingHorizontal: 16,
					gap: 16,
					flex: snapIndex === 1 ? 1 : undefined,
				}}
			>
				<BottomSheetTextInput
					value={value}
					onChangeText={onChangeText}
					multiline
					selectionColor={theme.colors.primary}
					placeholderTextColor={theme.colors.onSurfaceDisabled}
					placeholder={placeholder}
					autoComplete="off"
					autoCorrect={false}
					spellCheck={false}
					editable={!disabled}
					autoCapitalize="none"
					maxLength={maxLength}
					style={{
						minHeight: 120,
						flex: snapIndex === 1 ? 1 : undefined,
						paddingTop: 16,
						paddingBottom: 16,
						textAlignVertical: "top",
						color: theme.palette.text.primary,
						fontSize: 16,
					}}
				/>
			</BottomSheetView>
		</BottomSheet>
	);
};

export default CustomBottomSheetTextInput;
